import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom'; // Importado para redirecionamento
import './RegisterDesramaPopup.css';
import { API_BASE_URL } from "../../../../config";
import { FaCalendarPlus, FaEdit, FaTimes, FaArrowLeft, FaTrashAlt, FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';

const initialPrevisaoState = { previsao: '' };
const initialFulfillmentState = { altura: '', numero: '', data: '' };

const RegisterDesramaPopup = ({ isOpen, onClose, imovelId, onUpdateNeeded }) => {
    const [previsoes, setPrevisoes] = useState([]);
    const [previsaoData, setPrevisaoData] = useState(initialPrevisaoState);
    const [fulfillmentData, setFulfillmentData] = useState(initialFulfillmentState);
    const [selectedPrevisao, setSelectedPrevisao] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDeleteId, setItemToDeleteId] = useState(null);
    const navigate = useNavigate(); // Hook para navegação

    const formatDate = (dateString) => {
        return dateString ? new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : "N/A";
    };

    const fetchPrevisoes = useCallback(async () => {
        if (!imovelId) return;

        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/imoveis/${imovelId}/desramas/previsoes`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` // Usa o token JWT
                }
            });

            if (response.status === 401 || response.status === 400) {
                localStorage.removeItem('token');
                navigate('/');
                return;
            }

            if (!response.ok) {
                if (response.status === 404) {
                    setPrevisoes([]);
                } else {
                    throw new Error("Erro ao carregar previsões pendentes.");
                }
            } else {
                const data = await response.json();
                setPrevisoes(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            setError(err.message);
            setPrevisoes([]);
        } finally {
            setIsLoading(false);
        }
    }, [imovelId, navigate]);

    const resetState = useCallback(() => {
        setPrevisoes([]);
        setPrevisaoData(initialPrevisaoState);
        setFulfillmentData(initialFulfillmentState);
        setSelectedPrevisao(null);
        setError('');
        setSuccess('');
        setShowDeleteConfirmation(false);
        setItemToDeleteId(null);
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchPrevisoes();
        } else {
            setTimeout(resetState, 300);
        }
    }, [isOpen, fetchPrevisoes, resetState]);

    const handlePrevisaoChange = (e) => setPrevisaoData({ ...previsaoData, [e.target.name]: e.target.value });
    const handleFulfillmentChange = (e) => setFulfillmentData({ ...fulfillmentData, [e.target.name]: e.target.value });

    const handlePrevisaoSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        if (!token) {
            setError("Sessão expirada. Faça login novamente.");
            navigate('/');
            return;
        }
        if (!previsaoData.previsao) {
            setError("O campo 'previsão' é obrigatório!");
            return;
        }
        setIsSubmitting(true);
        setError('');
        setSuccess('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/imoveis/${imovelId}/desramas/previsao`, {
                method: "POST",
                headers: { "Content-Type": "application/json", 'Authorization': `Bearer ${token}` }, // Usa o token JWT
                body: JSON.stringify(previsaoData),
            });

            if (response.status === 401 || response.status === 400) {
                localStorage.removeItem('token');
                navigate('/');
                return;
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Erro ao registrar a previsão.");
            }
            setSuccess("Previsão registrada com sucesso!");
            setPrevisaoData(initialPrevisaoState);
            fetchPrevisoes();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFulfillmentSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        if (!token) {
            setError("Sessão expirada. Faça login novamente.");
            navigate('/');
            return;
        }
        const { altura, numero, data } = fulfillmentData;
        if (!altura || !numero || !data) {
            setError("Todos os campos (altura, número e data) são obrigatórios!");
            return;
        }
        setIsSubmitting(true);
        setError('');
        setSuccess('');
        try {
            const formattedData = { ...fulfillmentData, altura: altura.replace(",", ".") };
            const response = await fetch(`${API_BASE_URL}/api/desramas/${selectedPrevisao.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", 'Authorization': `Bearer ${token}` }, // Usa o token JWT
                body: JSON.stringify(formattedData),
            });

            if (response.status === 401 || response.status === 400) {
                localStorage.removeItem('token');
                navigate('/');
                return;
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Falha ao registrar a desrama.");
            }
            setSuccess("Desrama registrada com sucesso na previsão!");
            setSelectedPrevisao(null);
            fetchPrevisoes();
            if (onUpdateNeeded) onUpdateNeeded();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDelete = (id) => {
        setItemToDeleteId(id);
        setShowDeleteConfirmation(true);
    };

    const cancelDelete = () => {
        setItemToDeleteId(null);
        setShowDeleteConfirmation(false);
    };

    const executeDelete = async () => {
        if (!itemToDeleteId) return;

        const token = localStorage.getItem('token');
        if (!token) {
            setError("Sessão expirada. Faça login novamente.");
            navigate('/');
            return;
        }

        setIsSubmitting(true);
        setError('');
        
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/imoveis/${imovelId}/desramas/${itemToDeleteId}`,
                { headers: { 'Authorization': `Bearer ${token}` } } // Usa o token JWT
            );

            if (response.status === 200) {
                setSuccess("Previsão de desrama excluída com sucesso!");
                setPrevisoes(prevData => prevData.filter(previsao => previsao.id !== itemToDeleteId));
            }

        } catch (err) {
            if (err.response && (err.response.status === 401 || err.response.status === 400)) {
                localStorage.removeItem('token');
                navigate('/');
            } else {
                console.error("Erro ao excluir previsão de desrama:", err);
                setError(err.response?.data?.error || "Erro ao excluir o registro.");
            }
        } finally {
            setIsSubmitting(false);
            cancelDelete();
        }
    };

    if (!isOpen) return null;

    const renderMainView = () => (
        <>
            <form onSubmit={handlePrevisaoSubmit}>
                <section className="register-desrama-section">
                    <h3 className="register-desrama-section-title">Adicionar Nova Previsão de Desrama</h3>
                    <div className="previsao-form-group">
                        <input
                            type="date"
                            name="previsao"
                            value={previsaoData.previsao}
                            onChange={handlePrevisaoChange}
                            className="form-input"
                        />
                        <button type="submit" className="modal-btn btn-secondary" disabled={isSubmitting}>
                            <FaCalendarPlus /> {isSubmitting ? 'Registrando...' : 'Registrar Previsão'}
                        </button>
                    </div>
                </section>
            </form>
            <div className="register-desrama-divider"></div>
            <section className="register-desrama-section">
                <h3 className="register-desrama-section-title">Previsões Pendentes</h3>
                <div className="previsoes-list-container">
                    {isLoading ? <p>Carregando...</p> :
                     previsoes.length === 0 ? <p className="empty-message">Nenhuma previsão pendente.</p> :
                     (
                         <ul className="previsoes-list">
                             {previsoes.map((prev) => (
                                 <li key={prev.id} className="previsao-item">
                                     <span>{formatDate(prev.previsao)}</span>
                                     <div className="previsao-actions">
                                         <button onClick={() => setSelectedPrevisao(prev)} className="modal-btn btn-success">
                                             <FaEdit /> Registrar
                                         </button>
                                         <button onClick={() => confirmDelete(prev.id)} className="action-btn-previsao-delete" title="Excluir Previsão">
                                             <FaTrashAlt />
                                         </button>
                                     </div>
                                 </li>
                             ))}
                         </ul>
                     )
                    }
                </div>
            </section>
        </>
    );

    const renderFulfillmentView = () => (
        <form onSubmit={handleFulfillmentSubmit}>
            <section className="register-desrama-section">
                <h3 className="register-desrama-section-title">
                    Registrando Desrama para {formatDate(selectedPrevisao.previsao)}
                </h3>
                <div className="form-grid">
                    <div className="input-group">
                        <label htmlFor="altura" className="form-label">Altura (m)</label>
                        <input id="altura" name="altura" type="text" value={fulfillmentData.altura} onChange={handleFulfillmentChange} className="form-input" placeholder="Ex: 5,50"/>
                    </div>
                    <div className="input-group">
                        <label htmlFor="numero" className="form-label">Número</label>
                        <input id="numero" name="numero" type="number" value={fulfillmentData.numero} onChange={handleFulfillmentChange} className="form-input" />
                    </div>
                    <div className="input-group full-width">
                        <label htmlFor="data" className="form-label">Data da Execução</label>
                        <input id="data" name="data" type="date" value={fulfillmentData.data} onChange={handleFulfillmentChange} className="form-input" />
                    </div>
                </div>
            </section>
        </form>
    );

    return (
        <>
            {showDeleteConfirmation && (
                <div className="delete-confirmation-overlay" onClick={cancelDelete}>
                    <div className="delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
                        <FaExclamationTriangle className="warning-icon" />
                        <h3>Confirmar Exclusão</h3>
                        <p>Tem certeza que deseja excluir esta previsão de desrama? A ação não pode ser desfeita.</p>
                        <div className="modal-footer-stacked">
                            <button className="btn-danger-stacked" onClick={executeDelete} disabled={isSubmitting}>
                                <FaTrashAlt /> {isSubmitting ? 'Excluindo...' : 'Confirmar Exclusão'}
                            </button>
                            <button className="btn-link-stacked" onClick={cancelDelete} disabled={isSubmitting}>
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="register-desrama-overlay" onClick={onClose}>
                <div className="register-desrama-content" onClick={(e) => e.stopPropagation()}>
                    <header className="register-desrama-header">
                        <h2 className="register-desrama-title">Registrar Desrama</h2>
                        <button className="register-desrama-close-btn" onClick={onClose}>&times;</button>
                    </header>

                    <main className="register-desrama-body">
                        {error && <div className="feedback-message error-message" onClick={() => setError('')}>{error}</div>}
                        {success && <div className="feedback-message success-message" onClick={() => setSuccess('')}>{success}</div>}
                        {selectedPrevisao ? renderFulfillmentView() : renderMainView()}
                    </main>
                    
                    <footer className="register-desrama-footer">
                        {selectedPrevisao ? (
                            <>
                                <button type="button" className="modal-btn btn-cancel" onClick={() => setSelectedPrevisao(null)}>
                                    <FaArrowLeft /> Voltar
                                </button>
                                <button type="button" className="modal-btn btn-success" onClick={handleFulfillmentSubmit} disabled={isSubmitting}>
                                    <FaEdit /> {isSubmitting ? 'Salvando...' : 'Salvar Desrama'}
                                </button>
                            </>
                        ) : (
                            <button type="button" className="modal-btn btn-cancel" onClick={onClose}>
                                <FaTimes /> Fechar
                            </button>
                        )}
                    </footer>
                </div>
            </div>
        </>
    );
};

export default RegisterDesramaPopup;